/**
 * Fit a brand logo into a square canvas, centered (contain), then export PNG.
 * Transparent padding for non-square sources.
 */

const LOGO_SIZE = 512;
const ALLOWED = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read logo image"));
    };
    img.src = url;
  });
}

function canvasToPngFile(canvas: HTMLCanvasElement, baseName: string): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode logo"));
          return;
        }
        const name = `${baseName.replace(/\.[^.]+$/, "") || "logo"}.png`;
        resolve(new File([blob], name, { type: "image/png" }));
      },
      "image/png",
      0.92,
    );
  });
}

/**
 * Resize + center the selected logo into a 512×512 PNG (contain fit).
 */
export async function normalizeBrandLogoFile(file: File): Promise<File> {
  if (!ALLOWED.has(file.type)) {
    throw new Error("Logo must be JPEG, PNG, WebP, or GIF");
  }

  const img = await loadImage(file);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) {
    throw new Error("Invalid logo dimensions");
  }

  const canvas = document.createElement("canvas");
  canvas.width = LOGO_SIZE;
  canvas.height = LOGO_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas not available");
  }

  ctx.clearRect(0, 0, LOGO_SIZE, LOGO_SIZE);

  const scale = Math.min(LOGO_SIZE / srcW, LOGO_SIZE / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const dx = (LOGO_SIZE - drawW) / 2;
  const dy = (LOGO_SIZE - drawH) / 2;
  ctx.drawImage(img, dx, dy, drawW, drawH);

  return canvasToPngFile(canvas, file.name);
}
